import { Actions, Async, Dispatchers } from '@client/new/helpers/types';
import { getDispatchers } from '@client/new/helpers/types-utils';
import { trackSessionEvent } from '@services/session-tracker/sessionTracker';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ExtrasModel } from '../model/extras.model';
import { ContentLegacyGuide } from '../model/extras.domain';
import {
  defaultValues,
  toMixpanelContentType,
  toUpdateContentInput,
} from '../viewModel/extras.mappers';
import { toErrors } from '@client/new/helpers/forms';
import { trackEvent } from '@services/tracking';
import toast from 'react-hot-toast';
import { useKeepScrollPosition } from '@client/new/hooks/useKeepScrollPosition';
import { contentLegacyGuideSchema } from '../viewModel/extras.validation';
import {
  createFormRegister,
  createOnChangeHandler,
  ExtendedFormRegister,
  getFormMode,
} from '../viewModel/formHelpers';

type LegacyGuideActionMap = {
  Edit: ContentLegacyGuide['value'];
  Cancel: null;
};

type LegacyGuideVMAction = Actions<LegacyGuideActionMap>;

type LegacyGuideMode = 'none' | 'edit';

type LegacyGuideState = {
  mode: LegacyGuideMode;
  content: ContentLegacyGuide['value'];
};

type LegacyGuideViewModelParams = {
  model: ExtrasModel;
};

export type LegacyGuideViewModel = LegacyGuideState &
  Dispatchers<LegacyGuideActionMap> & {
    register: ExtendedFormRegister<ContentLegacyGuide['value']>;
    errors: any;
    values: ContentLegacyGuide['value'];
    onChange: (name: keyof ContentLegacyGuide['value'], value: any) => void;
  };

export const useLegacyGuideViewModel = (
  p: LegacyGuideViewModelParams,
): Async<LegacyGuideViewModel> => {
  const [showErrors, setShowErrors] = useState<boolean>(false);

  const [state, setState] = useState<LegacyGuideState>({
    mode: 'none',
    content: { ...defaultValues['ContentLegacyGuide'] },
  });

  useKeepScrollPosition(state.mode, 'none');

  const form = useForm<ContentLegacyGuide['value']>({
    reValidateMode: 'onBlur',
    defaultValues: defaultValues['ContentLegacyGuide'],
    resolver: zodResolver(contentLegacyGuideSchema),
  });

  useEffect(() => {
    if (state.content && Object.keys(state.content).length > 0) {
      form.reset({ ...state.content });
    }
  }, [form, state.content]);

  const values = form.watch();

  const onSubmit = (value: ContentLegacyGuide['value']) => {
    // Merge form values with state content to preserve fields not in schema (e.g., purchaseInfo)
    const mergedValue = { ...state.content, ...value };
    p.model
      .update(
        state.content.id,
        toUpdateContentInput({
          type: 'ContentLegacyGuide',
          value: mergedValue,
        }),
      )
      .then(() => {
        setState((prev) => ({
          ...prev,
          content: mergedValue,
        }));

        trackEvent({
          type: 'Extras Block Saved',
          payload: {
            title: value.title,
            type: toMixpanelContentType('ContentLegacyGuide'),
            'video time': 0,
            'video aspect ratio': 'n/a',
            'Comments on': false,
          },
        });
      })
      .catch((error) => {
        console.error('Error updating Legacy Guide:', error);
        toast.error('Failed to update Legacy Guide content');
      });
  };

  const onError = () => {
    setShowErrors(true);
  };

  const register = createFormRegister({
    form,
    onSubmit,
    onError,
    showErrors,
    mode: getFormMode(state.mode),
  });

  const onChange = createOnChangeHandler({
    form,
    onSubmit,
    onError,
    mode: getFormMode(state.mode),
  });

  const dispatchers = getDispatchers<LegacyGuideActionMap>(
    _dispatch(setState, p.model),
  );

  return {
    type: 'fetched',
    value: {
      ...state,
      ...dispatchers,
      register,
      onChange,
      values,
      errors: showErrors ? toErrors(form.formState.errors) : null,
    },
  };
};

const _dispatch =
  (
    setState: React.Dispatch<React.SetStateAction<LegacyGuideState>>,
    model: ExtrasModel,
  ) =>
  (a: LegacyGuideVMAction) => {
    trackSessionEvent('ViewAction', a);
    switch (a.type) {
      case 'Edit':
        return setState((prev) => ({
          ...prev,
          content: a.payload,
          mode: 'edit',
        }));

      case 'Cancel':
        return setState(onCancel);
    }
  };

const onCancel = (prev: LegacyGuideState): LegacyGuideState => {
  return {
    ...prev,
    mode: 'none',
    content: { ...defaultValues['ContentLegacyGuide'] },
  };
};
